import { useEffect, forwardRef, Ref } from 'react';
import { cn } from '../../../utils';
import { useMergedState } from 'rc-util';
import { AcceptsSetFormItemError } from '../FormItem/FormItem';

export type CheckboxProps = {
    className?: string | object;
    legendText?: string;
    name: string;
    onChange?: (value: boolean) => void;
    id: string;
    label?: string;
    description?: string;
    disabled?: boolean;
    value?: boolean;
    error?: string;
} & AcceptsSetFormItemError;

export default forwardRef(function Checkbox(
    {
        className,
        id,
        disabled = false,
        label,
        description,
        legendText,
        name,
        onChange,
        value = false,
        error,
    }: CheckboxProps,
    ref: Ref<HTMLInputElement>,
) {
    const [mergedActiveKey, setMergedActiveKey] = useMergedState<boolean>(
        false,
        {
            defaultValue: value,
        },
    );

    useEffect(() => {
        setMergedActiveKey(value);
    }, [value]);

    return (
        <fieldset>
            {legendText && (
                <legend className="alli-sr-only">{legendText}</legend>
            )}
            <div className="alli-space-y-5">
                <div
                    data-testid={`checkbox-option-${id}`}
                    key={`checkbox-option-${id}`}
                    className={cn(
                        'alli-relative alli-flex alli-items-start',
                        {
                            'alli-cursor-not-allowed': disabled,
                        },
                        className,
                    )}>
                    <div className="alli-flex alli-h-6 alli-items-center">
                        <input
                            onChange={() => {
                                setMergedActiveKey(!mergedActiveKey);
                                onChange && onChange(!mergedActiveKey);
                            }}
                            id={id}
                            aria-describedby={`${id}-description`}
                            name={name}
                            type="checkbox"
                            disabled={disabled}
                            checked={mergedActiveKey}
                            className={cn(
                                'alli-h-4 alli-w-4 alli-rounded alli-border-gray-700 alli-text-blue-600 focus:alli-ring-blue-600',
                                {
                                    'alli-border-gray-400': disabled,
                                    'alli-text-gray-600':
                                        disabled && mergedActiveKey,
                                    'alli-border-red-400': error,
                                },
                            )}
                            ref={ref}
                        />
                    </div>
                    <div className="alli-ml-3 alli-text-sm alli-leading-6">
                        {label && (
                            <label
                                htmlFor={id}
                                className={cn(
                                    'alli-select-none alli-font-medium alli-text-blue-gray-700',
                                    {
                                        'alli-text-gray-600': disabled,
                                    },
                                )}>
                                {label}
                            </label>
                        )}
                        {description && (
                            <p
                                id={`${id}-description`}
                                className={cn('alli-text-gray-750', {
                                    'alli-text-gray-600': disabled,
                                })}>
                                {description}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </fieldset>
    );
});
